import React from 'react';
import { CheckCircle, Target } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-bg pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About Brown & Associates
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Helping small businesses make sense of their numbers
          </p>
        </div>
      </section>

      {/* About Brown & Associates */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#64B7B7' }}>
              About Brown & Associates
            </h2>
          </div>
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl">
              Brown & Associates works with small businesses that need financials they can rely on.
            </p>
            <p className="text-xl">
              Many business owners start with good intentions, but over time their books become harder to trust. Transactions pile up, reports stop telling a clear story, and tax season becomes more stressful than it should be.
            </p>
            <p className="text-xl">
              We help organize books, rebuild accounting systems when needed, and create financial reporting that actually reflects what is happening in the business.
            </p>
          </div>
        </div>
      </section>

      {/* About Joede Brown - with Photo */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#64B7B7' }}>
              About Joede Brown
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Photo */}
              <div className="bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center p-12 md:p-16">
                <div className="w-full max-w-sm">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_86a06c09-5c72-45d0-9171-76f85c99b523/artifacts/f128sv36_Screenshot_20251124-155057_Photoroom.png" 
                    alt="Joede Brown, Principal of Brown & Associates"
                    className="w-full h-auto rounded-lg shadow-2xl object-contain"
                  />
                </div>
              </div>
              
              {/* Bio */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="space-y-6">
                  <p className="text-xl text-gray-700 leading-relaxed">
                    Joede Brown is the principal of Brown & Associates. She works with small businesses that need more than basic bookkeeping — they need financials they can rely on.
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    With a background in bookkeeping and a <span className="font-bold text-[#64B7B7]">Master's degree in Accounting</span>, Joede helps clients clean up messy books, identify inconsistencies, and build accounting systems that support better business decisions.
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    Her work ensures the numbers are not just recorded—but accurate, complete, and usable.
                  </p>
                  
                  <div className="mt-8">
                    <a 
                      href="https://calendly.com/joede1123"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold px-8 py-3 rounded-lg font-bold inline-block"
                    >
                      Schedule a Consultation
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#64B7B7' }}>
              What Makes Brown & Associates Different
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-2xl shadow-lg border-t-4" style={{ borderColor: '#64B7B7' }}>
            <div className="space-y-8">
              <div className="flex items-start">
                <Target className="w-8 h-8 mr-4 flex-shrink-0 mt-1" style={{ color: '#B89A4E' }} />
                <p className="text-xl text-gray-700 leading-relaxed">
                  Many bookkeeping services focus only on categorizing transactions.
                </p>
              </div>
              
              <div className="flex items-start">
                <CheckCircle className="w-8 h-8 mr-4 flex-shrink-0 mt-1" style={{ color: '#78C4C4' }} />
                <div>
                  <p className="text-xl text-gray-700 leading-relaxed mb-4">
                    <span className="font-bold text-[#64B7B7]">Brown & Associates focuses on whether the numbers actually make sense.</span>
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    That means reviewing financials, identifying issues, and making sure the books reflect what is really happening in the business.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-bg-reverse py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-white mb-6">
            Ready to Make Sense of Your Numbers?
          </h3>
          <a 
            href="https://calendly.com/joede1123"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-10 py-4 rounded-lg text-lg font-bold inline-block"
          >
            Schedule a Consultation
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
